import { Injectable } from '@angular/core';
import { SignalrMessageType } from '@epicuro-next/constants/platform/signalr';
import { SignalRService } from '@epicuro-next/data/signalr';
import { selectActiveAccountId } from '@epicuro-next/state/accounts-state';
import {
  initConnectTokenStateSuccess,
  selectConnectToken,
} from '@epicuro-next/state/connect-token-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { HubConnectionState } from '@microsoft/signalr';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { combineLatest, EMPTY, from, of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  skip,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import * as Sentry from '@sentry/angular';
import * as fromSignalr from './signalr.actions';

@Injectable()
export class SignalrEffects {
  private signalrParams$ = combineLatest([
    this.store.pipe(select(selectConnectToken), filter(isNonNullish)),
    this.store.pipe(select(selectActiveAccountId), filter(isNonNullish)),
  ]);

  constructor(
    private actions$: Actions,
    private signalrService: SignalRService,
    private store: Store,
  ) {}

  public onInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initConnectTokenStateSuccess),
      map(() => fromSignalr.initSignalrConnection()),
    ),
  );

  public initSocketConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromSignalr.initSignalrConnection),
      switchMap(() => this.signalrParams$),
      switchMap(([token, accountId]) =>
        from(
          this.signalrService.connect({
            token,
            accountId,
          }),
        ).pipe(
          catchError((err) => {
            const message = `Failed to connect signalR Service: ${
              err?.message ||
              err?.error ||
              JSON.stringify(err) ||
              'Unknown error'
            }`;
            console.error(message);
            Sentry.captureException(new Error(message));
            return EMPTY;
          }),
        ),
      ),
      map(() => fromSignalr.initSignalrConnectionSuccess()),
    ),
  );

  public stopSocketConnection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromSignalr.stopSignalrConnection),
        filter(
          () =>
            this.signalrService.hubConnection.state ===
            HubConnectionState.Connected,
        ),
        tap(() => {
          this.signalrService.stop();
        }),
      ),
    {
      dispatch: false,
    },
  );

  public sendSignalrMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromSignalr.sendSignalrMessage),
        concatMap((action) =>
          of(action).pipe(
            withLatestFrom(this.store.pipe(select(selectActiveAccountId))),
            tap(([{ methodName, params }, accountId]) =>
              this.signalrService.invoke(methodName, ...[accountId, ...params]),
            ),
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );

  public closeSignalrChannel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromSignalr.closeSignalrChannel),
        tap(({ message }) => this.signalrService.clearMessage(message)),
      ),
    {
      dispatch: false,
    },
  );

  public onConnectionShutdown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromSignalr.initSignalrConnectionSuccess),
      switchMap(() =>
        this.signalrService.messages<[string]>(
          SignalrMessageType.OnConnectionShutdown,
        ),
      ),
      map(() => fromSignalr.initSignalrConnection()),
    ),
  );

  public onConnectionReconnect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromSignalr.initSignalrConnectionSuccess),
      skip(1),
      map(() => fromSignalr.signalrReconnected()),
    ),
  );
}
